import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { HiOutlineChevronDown, HiOutlineChevronUp } from "react-icons/hi";

import API from "../api";
import { capture } from "../instrument";
import { SYNC_SOURCE_LABEL, REVIEW_STATUS } from "../utils";

export const FIELD_LABELS = {
  value: "Valeur",
  UMLS: "UMLS",
  sampleId: "Code echantillon",
  relatedCodes: "Sous-codes associes",
  incompatibleCodes: "Codes incompatibles",
  type: "Type",
  question: "Question",
  questionCode: "Code question",
  answerType: "Type de reponse",
  options: "Options",
  isRequired: "Obligatoire",
  patient: "Patient",
  patientQuestion: "Reformulation patient",
  patientOptions: "Options patient",
  ide: "IDE",
  ideQuestion: "Reformulation IDE",
  ideOptions: "Options IDE",
  canAnswerInPreRegistration: "Pré-enregistrement",
  canShowInPreRegistration: "Pré-enregistrement",
  guideline: "Recommandation",
  secretary: "Secrétaire",
  secretaryGuideline: "Recommandation secrétaire",
  patientGuideline: "Recommandation patient",
  ideGuideline: "Recommandation IDE",
  triggerGroups: "Groupes de conditions",
  groupsOperator: "Operateur",
  priority: "Priorite",
  tubes: "Tubes",
  name: "Nom",
  ubilabUrl: "URL Ubilab",
  enabledAtFrontDesk: "Visible à l'accueil",
};

const sourceEntries = (syncs, sources) => {
  const bySource = new Map();
  for (const entry of syncs || []) {
    bySource.set(entry.source, {
      source: entry.source,
      syncedAt: entry.syncedAt,
      missing: entry.deletedFromSource === true,
      missingAt: entry.deletedFromSourceAt,
      pending: entry.reviewStatus === REVIEW_STATUS.PENDING && entry.deletedFromSource !== true,
    });
  }
  for (const entry of sources || []) {
    bySource.set(entry.source, {
      source: entry.source,
      syncedAt: entry.syncedAt,
      missing: entry.missingFromSource === true,
      missingAt: entry.missingFromSourceAt,
      pending: bySource.get(entry.source)?.pending === true,
    });
  }
  return [...bySource.values()];
};

const SyncInfoBanner = ({ syncs, sources, docId, entityType, lastSyncChangedFields, lastSyncChangedAt }) => {
  const [showRaw, setShowRaw] = useState(false);

  const { data: overrides = [] } = useQuery({
    queryKey: ["sync-overrides", docId],
    enabled: Boolean(docId),
    queryFn: async () => {
      const response = await API.get(`/sync/override?docId=${docId}`);
      if (!response.ok) {
        const error = new Error(response.message);
        capture(error);
        throw error;
      }
      return response.data;
    },
  });

  const { data: snapshots = [], isLoading: isLoadingSnapshots } = useQuery({
    queryKey: ["sync-snapshot", entityType, docId],
    enabled: Boolean(showRaw && entityType && docId),
    queryFn: async () => {
      const response = await API.get(`/sync/snapshot?entityType=${entityType}&docId=${docId}`);
      if (!response.ok) {
        const error = new Error(response.message);
        capture(error);
        throw error;
      }
      return response.data;
    },
  });

  const entries = sourceEntries(syncs, sources);
  if (entries.length === 0) return null;

  const legacyRawEntries = (syncs || []).filter((entry) => entry.rawData).map((entry) => ({ source: entry.source, variant: null, payload: entry.rawData }));
  const rawEntries = entityType ? snapshots : legacyRawEntries;
  const canShowRaw = Boolean(entityType && docId) || legacyRawEntries.length > 0;

  return (
    <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
      {entries.map((entry) => (
        <div key={entry.source} className="flex items-center gap-2.5 px-4 py-2.5">
          <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${entry.missing ? "bg-amber-400" : entry.pending ? "bg-primary-500" : "bg-green-500"}`} />
          <span className="text-sm font-medium text-gray-900">{SYNC_SOURCE_LABEL[entry.source] || entry.source}</span>
          <span className="min-w-0 truncate text-xs text-gray-500">
            {entry.missing
              ? `disparu de la source${entry.missingAt ? ` le ${new Date(entry.missingAt).toLocaleDateString("fr-FR")}` : ""}`
              : entry.pending
                ? "modification source à valider"
                : `synchronisé${entry.syncedAt ? ` · ${new Date(entry.syncedAt).toLocaleDateString("fr-FR")}` : ""}`}
          </span>
        </div>
      ))}
      {lastSyncChangedAt && (
        <div className="px-4 py-2.5">
          <div className="mb-1.5 text-xs text-gray-500">Dernière modification depuis la source le {new Date(lastSyncChangedAt).toLocaleDateString("fr-FR")}</div>
          {lastSyncChangedFields?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {lastSyncChangedFields.map((field) => (
                <span key={field} className="rounded-md bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
                  {FIELD_LABELS[field] || field}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
      {overrides.length > 0 && (
        <div className="px-4 py-2.5">
          <div className="mb-1.5 text-xs text-gray-500">Modifié localement — vos valeurs sont prioritaires sur la source</div>
          <div className="flex flex-wrap gap-1">
            {overrides.map((override) => (
              <span key={override._id} className="rounded-md bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
                {FIELD_LABELS[override.field] || override.field}
              </span>
            ))}
          </div>
        </div>
      )}
      {canShowRaw && (
        <div className="px-4 py-2">
          <button type="button" onClick={() => setShowRaw(!showRaw)} className="flex items-center gap-1 text-xs text-gray-400 transition-colors hover:text-gray-600">
            {showRaw ? <HiOutlineChevronUp className="h-3 w-3" /> : <HiOutlineChevronDown className="h-3 w-3" />}
            Données brutes
          </button>
          {showRaw && (
            <div className="mt-2 space-y-2">
              {isLoadingSnapshots ? (
                <div className="text-xs text-gray-400">Chargement…</div>
              ) : rawEntries.length === 0 ? (
                <div className="text-xs text-gray-400">Aucune donnée brute disponible.</div>
              ) : (
                rawEntries.map((entry, index) => (
                  <div key={index}>
                    <div className="mb-1 text-xs font-medium text-gray-500">
                      {SYNC_SOURCE_LABEL[entry.source] || entry.source}
                      {entry.variant ? ` · ${entry.variant}` : ""}
                    </div>
                    <pre className="max-h-48 overflow-auto rounded-md bg-gray-50 p-2 text-xs text-gray-600">{JSON.stringify(entry.payload, null, 2)}</pre>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SyncInfoBanner;
